import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card, StatusPill } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, Trash, Copy } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const Conferences = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/conferences');
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...editing };
            if (editing.id) await api.put(`/conferences/${editing.id}`, payload);
            else await api.post('/conferences', payload);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat místnost?')) return;
        await api.delete(`/conferences/${id}`); load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Collaboration"
                title="Konferenční místnosti"
                description="Audio konferenční místnosti s PIN ochranou. Vytočte interní číslo a připojte více účastníků."
                actions={
                    <button onClick={() => setEditing({ name: '', extension_number: '', pin: '', moderator_pin: '', max_participants: 50, record: false, announce_user_count: true, require_pin: true, is_active: true })}
                            data-testid="conf-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nová místnost
                    </button>
                }
            />
            <Card>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné konferenční místnosti.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Číslo</th>
                                <th className="text-left">Název</th>
                                <th className="text-center">PIN</th>
                                <th className="text-center">Max účast.</th>
                                <th className="text-center">Recording</th>
                                <th className="text-center">Stav</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono font-bold">{c.extension_number}</td>
                                    <td>{c.name}</td>
                                    <td className="text-center">
                                        <StatusPill status={c.require_pin ? 'warning' : 'neutral'} label={c.require_pin ? 'PIN required' : 'open'} />
                                    </td>
                                    <td className="text-center font-mono">{c.max_participants}</td>
                                    <td className="text-center">
                                        <StatusPill status={c.record ? 'info' : 'neutral'} label={c.record ? 'on' : 'off'} />
                                    </td>
                                    <td className="text-center">
                                        <StatusPill status={c.is_active ? 'success' : 'neutral'} label={c.is_active ? 'aktivní' : 'vypnuto'} />
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => del(c.id)} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`conf-delete-${c.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal title="Nová konferenční místnost" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                        <Field label="Číslo (interní)" value={editing.extension_number} onChange={(v) => setEditing({ ...editing, extension_number: v })} required mono />
                        <Field label="PIN účastníka" value={editing.pin || ''} onChange={(v) => setEditing({ ...editing, pin: v })} type="password" mono />
                        <Field label="PIN moderátora" value={editing.moderator_pin || ''} onChange={(v) => setEditing({ ...editing, moderator_pin: v })} type="password" mono />
                        <Field label="Max účastníků" value={editing.max_participants} onChange={(v) => setEditing({ ...editing, max_participants: parseInt(v || '0', 10) })} type="number" mono />
                        <div className="space-y-2 mt-5">
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.require_pin} onChange={(e) => setEditing({ ...editing, require_pin: e.target.checked })} />
                                Vyžadovat PIN
                            </label>
                            <label className="block inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.record} onChange={(e) => setEditing({ ...editing, record: e.target.checked })} />
                                Nahrávat
                            </label>
                            <label className="block inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={!!editing.announce_user_count} onChange={(e) => setEditing({ ...editing, announce_user_count: e.target.checked })} />
                                Oznamovat počet účastníků
                            </label>
                        </div>
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="conf-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default Conferences;
